import * as React from 'react';
import { useState } from 'react';
import { Stamp } from '../Icon/icons';
import { Icon } from '../Icon/Icon';
import {
  root as rootCls,
  img as imgCls,
  stampBadge as stampCls,
  onlineDot as onlineDotCls,
} from './Avatar.css';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'display';
export type AvatarShape = 'square' | 'round';
/**
 * Avatar ring tone — drives the 2px coloured ring around the avatar
 * that signals sender identity.
 *
 *   - 'none'   — humans (no ring)
 *   - 'blue'   — specialist agent (Finch, Ellis, Varnan, etc.)
 *   - 'purple' — system agent (Smith, Mira)
 *   - 'teal'   — Workstream Agent
 */
export type AvatarRingTone = 'none' | 'blue' | 'purple' | 'teal';

export interface AvatarProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** Name — source for initials fallback + accessible label. */
  name: string;
  /** Image URL. Falls back to initials on error or absence. */
  src?: string;
  size?: AvatarSize;
  /** Default 'round' (Genesis v2 — was 'square' in legacy). */
  shape?: AvatarShape;
  /**
   * Ring colour denoting agent identity. Default 'none' (humans). A
   * `'purple'` ring (system agent — Smith / Mira) also renders the
   * inked-violet stamp glyph at the bottom-right.
   */
  ringTone?: AvatarRingTone;
  /**
   * Optional online presence dot. `undefined` hides it; `true` shows
   * green; `false` shows grey.
   */
  online?: boolean;
  /** React 19 ref-as-prop. */
  ref?: React.Ref<HTMLSpanElement>;
}

const STAMP_SIZE: Record<AvatarSize, number> = {
  xs: 8,
  sm: 10,
  md: 12,
  lg: 14,
  xl: 18,
  display: 24,
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean).slice(0, 2);
  if (parts.length === 0) return '?';
  return parts.map((p) => p[0]!.toUpperCase()).join('');
}

/**
 * Avatar — circular (default) or square user/agent avatar. Renders the
 * `src` image with initials fallback on error / absence; the initials
 * derive from `name`.
 *
 * Two semantic prop families:
 *   - `ringTone` — adds a 2px coloured ring denoting sender identity
 *     ('blue' specialist agent / 'purple' system agent / 'teal'
 *     workstream agent / 'none' human). A `'purple'` (system-agent) ring
 *     also renders the inked-violet stamp glyph bottom-right.
 *   - `online` — adds a small status dot at the bottom-right (`true`
 *     green, `false` grey, `undefined` hidden).
 *
 * Validated against the prototype's avatar treatments.
 */
export function Avatar({
  name,
  src,
  size = 'md',
  shape = 'round',
  ringTone = 'none',
  online,
  'aria-label': ariaLabel,
  ref,
  className,
  ...rest
}: AvatarProps) {
  const [errored, setErrored] = useState(false);
  const showImage = Boolean(src) && !errored;
  const stampPx = STAMP_SIZE[size];

  const cls = [rootCls({ size, shape, ringTone }), className].filter(Boolean).join(' ');

  return (
    <span ref={ref} className={cls} role="img" aria-label={ariaLabel ?? name} {...rest}>
      {showImage ? (
        <img className={imgCls} src={src} alt="" onError={() => setErrored(true)} />
      ) : (
        <span aria-hidden="true">{initials(name)}</span>
      )}
      {ringTone === 'purple' && (
        <span className={stampCls} aria-hidden="true">
          <Icon as={Stamp} size={stampPx} strokeWidth={2} />
        </span>
      )}
      {online !== undefined && <span className={onlineDotCls({ online })} aria-hidden="true" />}
    </span>
  );
}
Avatar.displayName = 'Avatar';
